<template>
  <div class="p-6 space-y-5">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-sm font-bold text-n-slate-12 flex items-center gap-2">
          <span class="i-lucide-calendar size-4 text-[#2781F6]" />
          <span>Prazos e Tarefas Processuais</span>
        </h3>
        <p class="text-xs text-n-slate-11 mt-0.5">
          Histórico e controle de todos os prazos cumpridos e em aberto deste cliente.
        </p>
      </div>

      <button
        @click="openAddModal"
        class="px-3.5 py-1.5 text-xs font-bold rounded-xl bg-[#2781F6] text-white hover:bg-[#2781F6]/90 transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
      >
        <span class="i-lucide-plus size-4" />
        Novo Prazo
      </button>
    </div>

    <!-- Filter Tabs -->
    <div class="flex gap-2 border-b border-n-weak pb-2 text-xs font-semibold">
      <button
        @click="filterStatus = 'pendentes'"
        :class="[
          'px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1.5',
          filterStatus === 'pendentes' ? 'bg-[#2781F6] text-white font-bold' : 'bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12'
        ]"
      >
        <span>Em Aberto</span>
        <span v-if="pendingTarefas.length" class="px-1.5 py-0.2 rounded-full bg-white/20 text-white text-[10px]">
          {{ pendingTarefas.length }}
        </span>
      </button>

      <button
        @click="filterStatus = 'concluidas'"
        :class="[
          'px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1.5',
          filterStatus === 'concluidas' ? 'bg-emerald-600 text-white font-bold' : 'bg-n-alpha-2 text-n-slate-11 hover:text-emerald-500'
        ]"
      >
        <span>Cumpridos / Arquivados</span>
        <span v-if="completedTarefas.length" class="px-1.5 py-0.2 rounded-full bg-white/20 text-white text-[10px]">
          {{ completedTarefas.length }}
        </span>
      </button>

      <button
        @click="filterStatus = 'todas'"
        :class="[
          'px-3 py-1.5 rounded-lg transition-all cursor-pointer',
          filterStatus === 'todas' ? 'bg-n-brand text-white font-bold' : 'bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12'
        ]"
      >
        Todos ({{ tarefas.length }})
      </button>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="text-center py-8 text-n-slate-10 text-xs">
      Carregando prazos do cliente...
    </div>

    <!-- Empty State -->
    <div
      v-else-if="filteredTarefas.length === 0"
      class="p-8 rounded-2xl border border-dashed border-n-weak text-center bg-n-solid-1/40"
    >
      <div class="p-3 rounded-full bg-n-alpha-2 text-n-slate-10 w-fit mx-auto mb-2">
        <span class="i-lucide-calendar-check size-6" />
      </div>
      <p class="text-xs font-semibold text-n-slate-12">
        Nenhum prazo encontrado nesta categoria.
      </p>
    </div>

    <!-- List of Tarefas with Left Stripe -->
    <div v-else class="space-y-3">
      <div
        v-for="task in filteredTarefas"
        :key="task.id"
        class="relative overflow-hidden rounded-2xl border border-n-weak/80 bg-n-solid-1 text-xs transition-all duration-150 shadow-sm"
        style="padding-left: 1.25rem; padding-right: 1rem; padding-top: 1rem; padding-bottom: 1rem;"
      >
        <!-- Tarja Lateral Esquerda Colorida (Design Mockup) -->
        <div
          class="absolute left-0 top-0 bottom-0 w-2.5 rounded-l-2xl"
          :style="{ backgroundColor: getIndicatorColor(task) }"
        />

        <div class="flex items-start justify-between gap-3">
          <div class="space-y-1.5 flex-1 min-w-0">
            <!-- Header Badges -->
            <div class="flex flex-wrap items-center gap-1.5">
              <button
                @click="openViewModal(task)"
                class="px-2.5 py-0.5 text-[10px] font-bold rounded-md bg-[#2781F6] text-white hover:bg-[#2781F6]/90 transition-all cursor-pointer shadow-2xs"
              >
                ver
              </button>

              <button
                @click="toggleStatus(task.id)"
                :class="[
                  'px-2.5 py-0.5 text-[10px] font-bold rounded-md transition-all cursor-pointer shadow-2xs',
                  task.status === 'concluida'
                    ? 'bg-amber-500/20 text-amber-500 hover:bg-amber-500 hover:text-black'
                    : 'bg-[#2781F6] text-white hover:bg-emerald-600'
                ]"
              >
                {{ task.status === 'concluida' ? 'Reabrir' : 'Cumprir' }}
              </button>

              <span
                v-if="task.priority === 'urgente'"
                class="px-1.5 py-0.2 text-[9px] font-black rounded-md bg-rose-600 text-white uppercase shadow-2xs"
              >
                🚨 Urgente
              </span>
              <span
                v-else-if="task.priority === 'alta'"
                class="px-1.5 py-0.2 text-[9px] font-bold rounded-md bg-amber-500 text-slate-950 uppercase shadow-2xs"
              >
                ⚠️ Alta
              </span>
              <span
                v-else-if="task.priority === 'media'"
                class="px-1.5 py-0.2 text-[9px] font-bold rounded-md bg-blue-500/20 text-blue-400 border border-blue-500/40 uppercase"
              >
                🔵 Média
              </span>
              <span
                v-else
                class="px-1.5 py-0.2 text-[9px] font-bold rounded-md bg-slate-500/20 text-slate-300 border border-slate-500/40 uppercase"
              >
                ⚪ Baixa
              </span>
            </div>

            <!-- Title -->
            <h4
              :class="[
                'font-bold text-sm leading-snug',
                task.status === 'concluida' ? 'line-through text-n-slate-10' : 'text-n-slate-12'
              ]"
            >
              {{ task.title }}
            </h4>

            <!-- Description -->
            <p v-if="task.description" class="text-xs text-n-slate-11 line-clamp-2 leading-relaxed">
              {{ task.description }}
            </p>

            <!-- Footer: Process Number, Fatal Badge & Agent -->
            <div class="flex flex-wrap items-center gap-2 pt-2 border-t border-n-weak/60 text-[10px]">
              <span v-if="task.process_number" class="text-n-slate-10 font-medium">
                ⚖️ Proc: {{ task.process_number }}
              </span>

              <span
                v-if="task.is_fatal_deadline"
                class="px-1.5 py-0.2 text-[9px] font-black rounded-md bg-amber-400 text-black flex items-center gap-0.5 tracking-wider shadow-2xs uppercase"
              >
                ⚡ PRAZO FATAL
              </span>

              <span v-if="task.assigned_user" class="text-n-slate-12 font-bold flex items-center gap-1 ml-auto">
                <span class="i-lucide-user-check size-3 text-[#2781F6]" />
                <span>Resp: {{ task.assigned_user.name }}</span>
              </span>
            </div>
          </div>

          <!-- Stacked Date & Time on Right -->
          <div class="text-right shrink-0 leading-tight">
            <div
              class="text-xs font-extrabold whitespace-nowrap flex items-center justify-end gap-1"
              :class="task.overdue ? 'text-rose-600 dark:text-rose-400' : isTaskToday(task.due_date) ? 'text-amber-500 font-black' : 'text-n-slate-12'"
            >
              <span class="i-lucide-calendar size-3.5" />
              <span>{{ formatDate(task.due_date) }}</span>
            </div>
            <div v-if="task.due_time" class="text-[10px] font-semibold text-n-slate-10 mt-0.5">
              às {{ task.due_time }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Pop-up de Visualização Completa -->
    <div
      v-if="showViewModal && selectedTask"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
    >
      <div class="bg-n-solid-1 border border-n-weak rounded-2xl w-full max-w-lg shadow-2xl p-6 space-y-4 text-xs text-left max-h-[90vh] flex flex-col">
        <div class="flex items-start justify-between border-b border-n-weak pb-3">
          <div class="space-y-1">
            <div class="flex flex-wrap items-center gap-1.5">
              <span
                :class="[
                  'px-2 py-0.5 text-[10px] font-bold rounded-md uppercase',
                  selectedTask.status === 'concluida' ? 'bg-emerald-500 text-white' : 'bg-[#2781F6] text-white'
                ]"
              >
                {{ selectedTask.status === 'concluida' ? '✅ CONCLUÍDO' : '⏳ PENDENTE' }}
              </span>

              <span
                v-if="selectedTask.is_fatal_deadline"
                class="px-2 py-0.5 text-[10px] font-black rounded-md bg-amber-400 text-black uppercase"
              >
                ⚡ PRAZO FATAL
              </span>

              <span
                v-if="selectedTask.overdue"
                class="px-2 py-0.5 text-[10px] font-black rounded-md bg-rose-600 text-white"
              >
                ⚠️ VENCIDA HÁ {{ selectedTask.days_overdue }} DIAS
              </span>
            </div>
            <h3 class="text-base font-bold text-n-slate-12 leading-snug pt-1">
              {{ selectedTask.title }}
            </h3>
          </div>

          <button
            @click="showViewModal = false"
            class="p-1 rounded-md text-n-slate-10 hover:text-n-slate-12 cursor-pointer shrink-0"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="flex-1 overflow-y-auto space-y-3.5 pr-1 no-scrollbar text-xs">
          <div class="grid grid-cols-2 gap-3 p-3 rounded-xl bg-n-solid-2/60 border border-n-weak">
            <div>
              <div class="text-[10px] uppercase font-bold text-n-slate-10">Data Limite</div>
              <div class="text-sm font-bold text-n-slate-12 mt-0.5">
                📅 {{ formatDate(selectedTask.due_date) }}
                <span v-if="selectedTask.due_time" class="text-xs text-n-slate-10 font-normal">às {{ selectedTask.due_time }}</span>
              </div>
            </div>

            <div>
              <div class="text-[10px] uppercase font-bold text-n-slate-10">Processo Vinculado</div>
              <div class="text-xs font-bold text-n-slate-12 mt-0.5">
                ⚖️ {{ selectedTask.process_number || 'Não informado' }}
              </div>
            </div>
          </div>

          <div class="space-y-1">
            <div class="text-[10px] uppercase font-bold text-n-slate-10">Descrição / Decisão</div>
            <div class="p-3.5 rounded-xl bg-n-background border border-n-weak text-n-slate-12 text-xs leading-relaxed whitespace-pre-wrap max-h-60 overflow-y-auto">
              {{ selectedTask.description || 'Nenhuma descrição detalhada informada.' }}
            </div>
          </div>

          <div v-if="selectedTask.assigned_user" class="p-3 rounded-xl bg-n-solid-2/40 border border-n-weak flex items-center gap-2">
            <span class="i-lucide-user-check size-4 text-[#2781F6]" />
            <div class="text-xs">
              <span class="text-n-slate-10">Agente Responsável: </span>
              <strong class="text-n-slate-12">{{ selectedTask.assigned_user.name }}</strong>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-between pt-3 border-t border-n-weak">
          <button
            @click="toggleStatus(selectedTask.id); showViewModal = false;"
            :class="[
              'px-4 py-2 font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-xs',
              selectedTask.status === 'concluida'
                ? 'bg-amber-500 text-slate-950 hover:bg-amber-600'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            ]"
          >
            <span :class="selectedTask.status === 'concluida' ? 'i-lucide-rotate-ccw' : 'i-lucide-check'" class="size-4" />
            <span>{{ selectedTask.status === 'concluida' ? 'Reabrir Prazo' : 'Marcar como Cumprido' }}</span>
          </button>

          <button
            @click="showViewModal = false"
            class="px-4 py-2 rounded-xl bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3 font-semibold cursor-pointer"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Adicionar Novo Prazo -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
    >
      <div class="bg-n-solid-1 border border-n-weak rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-3.5 text-xs text-left">
        <div class="flex items-center justify-between border-b border-n-weak pb-2">
          <h3 class="text-sm font-bold text-n-slate-12 flex items-center gap-1.5">
            <span class="i-lucide-calendar-plus size-4 text-[#2781F6]" />
            <span>Novo Prazo / Tarefa</span>
          </h3>
          <button
            @click="showModal = false"
            class="p-1 rounded-md text-n-slate-10 hover:text-n-slate-12 cursor-pointer"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <form @submit.prevent="saveNewPrazo" class="space-y-3">
          <div class="space-y-1">
            <label class="font-bold text-n-slate-12">Título do Prazo *</label>
            <input
              v-model="form.title"
              required
              placeholder="Ex: Protocolar Recurso, Apresentar Quesitos..."
              class="w-full px-3 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6]"
            />
          </div>

          <div class="space-y-1">
            <label class="font-medium text-n-slate-11">Descrição / Instruções</label>
            <textarea
              v-model="form.description"
              rows="2"
              placeholder="Descreva a providência ou decisão..."
              class="w-full px-3 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6]"
            ></textarea>
          </div>

          <div class="grid grid-cols-2 gap-2">
            <div class="space-y-1">
              <label class="font-bold text-n-slate-12">Data Limite *</label>
              <input
                v-model="form.due_date"
                required
                type="date"
                class="w-full px-2.5 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6]"
              />
            </div>
            <div class="space-y-1">
              <label class="font-bold text-n-slate-12">Hora Limite</label>
              <input
                v-model="form.due_time"
                type="time"
                class="w-full px-2.5 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6]"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-2">
            <div class="space-y-1">
              <label class="font-bold text-n-slate-12">Prioridade</label>
              <select
                v-model="form.priority"
                class="w-full px-2.5 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6] cursor-pointer"
              >
                <option value="urgente">🚨 Urgente</option>
                <option value="alta">⚠️ Alta</option>
                <option value="media">🔵 Média</option>
                <option value="baixa">⚪ Baixa</option>
              </select>
            </div>

            <div class="space-y-1">
              <label class="font-medium text-n-slate-11">Número do Processo</label>
              <input
                v-model="form.process_number"
                placeholder="Ex: 1000425-20.2026.8.26.0383"
                class="w-full px-3 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6]"
              />
            </div>
          </div>

          <!-- Responsible Agent Selector -->
          <div class="space-y-1">
            <label class="font-bold text-n-slate-12">👤 Agente Responsável</label>
            <select
              v-model="form.assigned_user_id"
              class="w-full px-3 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6] cursor-pointer"
            >
              <option :value="null">Nenhum agente atribuído</option>
              <option v-for="agent in agentsList" :key="agent.id" :value="agent.id">
                👤 {{ agent.name }} ({{ agent.email }})
              </option>
            </select>
          </div>

          <label class="flex items-center gap-2 p-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-600 dark:text-rose-400 font-bold cursor-pointer select-none">
            <input
              type="checkbox"
              v-model="form.is_fatal_deadline"
              class="size-4 rounded text-rose-500 focus:ring-rose-500 cursor-pointer"
            />
            <span>⚡ Prazo Processual Fatal</span>
          </label>

          <div class="flex justify-end gap-2 pt-2 border-t border-n-weak">
            <button
              type="button"
              @click="showModal = false"
              class="px-3 py-1.5 rounded-lg bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3 cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="px-4 py-1.5 font-bold rounded-lg bg-[#2781F6] text-white hover:bg-[#2781F6]/90 cursor-pointer"
            >
              Salvar Prazo
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import TarefasAPI from 'dashboard/api/tarefas';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const tarefas = ref([]);
const isLoading = ref(false);
const filterStatus = ref('pendentes');

const showViewModal = ref(false);
const selectedTask = ref(null);
const showModal = ref(false);

const agentsList = computed(() => store.getters['agents/getAgents'] || []);

const pendingTarefas = computed(() =>
  tarefas.value.filter(t => t.status !== 'concluida')
);

const completedTarefas = computed(() =>
  tarefas.value.filter(t => t.status === 'concluida')
);

const filteredTarefas = computed(() => {
  if (filterStatus.value === 'pendentes') return pendingTarefas.value;
  if (filterStatus.value === 'concluidas') return completedTarefas.value;
  return tarefas.value;
});

const getBrasiliaTodayStr = () => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
};
const todayStr = getBrasiliaTodayStr();

const isTaskToday = (dateStr) => {
  return dateStr === todayStr;
};

// Indicator Color according to mockup
const getIndicatorColor = (task) => {
  if (task.status === 'concluida') return '#10b981'; // Verde esmeralda
  if (task.overdue) return '#ef4444'; // Vermelho vivo
  if (task.is_fatal_deadline) return '#ef4444'; // Vermelho
  if (task.priority === 'urgente') return '#ef4444'; // Vermelho
  if (task.priority === 'alta') return '#f59e0b'; // Âmbar / Laranja
  if (task.due_date === todayStr) return '#f59e0b'; // Âmbar para hoje
  return '#2781F6'; // Azul padrão
};

const form = ref({
  title: '',
  description: '',
  due_date: todayStr,
  due_time: '18:00',
  priority: 'alta',
  is_fatal_deadline: false,
  process_number: '',
  assigned_user_id: null,
});

const fetchTarefas = async () => {
  if (!props.selectedContact?.id) return;
  isLoading.value = true;
  try {
    const res = await TarefasAPI.get({ contact_id: props.selectedContact.id, tab: 'todas' });
    tarefas.value = res.data?.tarefas || [];
  } catch (e) {
    console.error('Error fetching contact tarefas:', e);
  } finally {
    isLoading.value = false;
  }
};

const openViewModal = (task) => {
  selectedTask.value = task;
  showViewModal.value = true;
};

const openAddModal = () => {
  const add = props.selectedContact?.additional_attributes || props.selectedContact?.additionalAttributes || {};
  let defaultProc = add.process_number || (add.processes && add.processes[0]?.number) || (add.processos && add.processos[0]?.number) || '';

  form.value = {
    title: '',
    description: '',
    due_date: todayStr,
    due_time: '18:00',
    priority: 'alta',
    is_fatal_deadline: false,
    process_number: defaultProc,
    assigned_user_id: null,
  };
  showModal.value = true;
};

const saveNewPrazo = async () => {
  if (!form.value.title.trim()) return;
  try {
    await TarefasAPI.create({
      tarefa: {
        ...form.value,
        contact_id: props.selectedContact.id,
      },
    });
    showModal.value = false;
    fetchTarefas();
    store.dispatch('tarefas/getSummary');
  } catch (e) {
    alert('Erro ao salvar prazo: ' + e.message);
  }
};

const toggleStatus = async (id) => {
  try {
    await TarefasAPI.toggleStatus(id);
    fetchTarefas();
    store.dispatch('tarefas/getSummary');
  } catch (e) {
    console.error(e);
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const p = dateStr.split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : dateStr;
};

watch(() => props.selectedContact?.id, () => {
  fetchTarefas();
});

onMounted(() => {
  fetchTarefas();
  store.dispatch('agents/get');
});
</script>
